import React, { useState, useEffect } from 'react';
import { X, Search, FolderOpen, Clock, ExternalLink, ChevronRight, Users, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleDriveService } from '../../services/googleDrive';
import { DriveFolder } from '../../types';

interface DriveFolderSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFolderSelected: (folder: DriveFolder) => void;
}

export const DriveFolderSearchModal: React.FC<DriveFolderSearchModalProps> = ({
  isOpen,
  onClose,
  onFolderSelected
}) => {
  const { firebaseUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<DriveFolder[]>([]);
  const [recentFolders, setRecentFolders] = useState<DriveFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [searchInJobsOnly, setSearchInJobsOnly] = useState(false);

  const driveService = GoogleDriveService.getInstance();

  useEffect(() => {
    if (isOpen) {
      loadRecentFolders();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const searchTimeout = setTimeout(() => {
        performSearch();
      }, 500); // Debounce search

      return () => clearTimeout(searchTimeout);
    } else {
      setSearchResults([]);
      setNextPageToken(undefined);
    }
  }, [searchTerm]);

  // Load recent Drive folders (via Firebase Functions)
  const loadRecentFolders = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError('');
      
      console.log('📂 Loading recent Drive folders...');
      // Use the Google Drive Service (calls Firebase Functions with service account)
      const folders = await driveService.getRecentFolders(firebaseUser, 10);
      
      console.log('✅ Loaded recent folders:', folders.length);
      setRecentFolders(folders);
    } catch (err: any) {
      console.error('❌ Error loading recent folders:', err);
      
      if (err.message?.includes('Insufficient permissions') || err.message?.includes('Drive access')) {
        setError('Google Drive access not configured. Please contact your administrator.');
      } else {
        setError(`Failed to load recent folders: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Search Drive folders (via Firebase Functions)
  const performSearch = async (pageToken?: string) => {
    if (!firebaseUser || !searchTerm.trim()) return;

    try {
      if (!pageToken) {
        setSearching(true);
        setSearchResults([]);
      }
      setError('');
      
      console.log('🔍 Searching Drive folders:', searchTerm, searchInJobsOnly ? '(Jobs folder only)' : '(all folders)');
      
      // Use the Google Drive Service (calls Firebase Functions with service account)
      const result = searchInJobsOnly 
        ? await driveService.searchInJobsFolder(searchTerm.trim(), firebaseUser, pageToken)
        : await driveService.searchFolders(searchTerm.trim(), firebaseUser, pageToken);
      
      console.log('✅ Found folders:', result.folders.length);
      
      if (pageToken) {
        // Append to existing results for pagination
        setSearchResults(prev => [...prev, ...result.folders]);
      } else {
        // Replace results for new search
        setSearchResults(result.folders);
      }
      
      setNextPageToken(result.nextPageToken);
    } catch (err: any) {
      console.error('❌ Error searching Drive folders:', err);
      
      if (err.message?.includes('Insufficient permissions') || err.message?.includes('Drive access')) {
        setError('Google Drive access not configured. Please contact your administrator.');
      } else {
        setError(`Failed to search folders: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setSearching(false);
    }
  };

  const handleLoadMore = () => {
    if (nextPageToken && !searching) {
      performSearch(nextPageToken);
    }
  };

  const handleFolderSelect = (folder: DriveFolder) => {
    console.log('📁 Folder selected:', folder);
    onFolderSelected(folder);
    onClose();
  };

  const handleViewInDrive = (folder: DriveFolder) => {
    window.open(folder.url, '_blank');
  };

  const resetModal = () => {
    setSearchTerm('');
    setSearchResults([]);
    setNextPageToken(undefined);
    setError('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  const displayFolders = searchTerm.trim() ? searchResults : recentFolders;
  const showLoadMore = searchTerm.trim() && nextPageToken && !searching;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FolderOpen className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Select Google Drive Folder</h2>
              <p className="text-sm text-gray-500">
                Choose a folder to link with your project
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={searchInJobsOnly ? "Search within Jobs folder..." : "Search for folders in your Google Drive..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
          
          {/* Search Scope Toggle */}
          <div className="mt-3 flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={searchInJobsOnly}
                onChange={(e) => {
                  setSearchInJobsOnly(e.target.checked);
                  // Clear current results when toggling
                  setSearchResults([]);
                  setNextPageToken(undefined);
                }}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">
                Search only in <span className="font-medium text-blue-600">Jobs folder</span>
              </span>
            </label>
          </div>
          
          {!searchTerm.trim() && (
            <p className="text-xs text-gray-500 mt-2">
              {searchInJobsOnly 
                ? "Start typing to search within the Jobs folder" 
                : "Start typing to search, or select from recent folders below"
              }
            </p>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Error Display */}
          {error && (
            <div className="p-6 bg-red-50 border-b border-red-200">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading folders...</p>
              </div>
            </div>
          )}

          {/* Folders List */}
          {!loading && (
            <div className="p-6">
              {/* Section Header */}
              <div className="flex items-center mb-4">
                {searchTerm.trim() ? (
                  <>
                    <Search className="w-4 h-4 text-gray-500 mr-2" />
                    <h3 className="text-sm font-medium text-gray-700">
                      {searchInJobsOnly ? (
                        <>
                          Search Results in <span className="text-blue-600">Jobs Folder</span> {searchResults.length > 0 && `(${searchResults.length})`}
                        </>
                      ) : (
                        <>
                          Search Results {searchResults.length > 0 && `(${searchResults.length})`}
                        </>
                      )}
                    </h3>
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4 text-gray-500 mr-2" />
                    <h3 className="text-sm font-medium text-gray-700">Recent Folders</h3>
                  </>
                )}
              </div>

              {/* Folders Grid */}
              {displayFolders.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm.trim() ? 'No folders found' : 'No recent folders'}
                  </h3>
                  <p className="text-gray-500">
                    {searchTerm.trim() 
                      ? (searchInJobsOnly 
                          ? 'No folders found in the Jobs folder. Try a different search term or uncheck the Jobs folder filter.'
                          : 'Try a different search term or check your spelling'
                        )
                      : 'Your recently accessed folders will appear here'
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {displayFolders.map((folder) => (
                    <div
                      key={folder.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div 
                        className="flex items-center space-x-3 flex-1 cursor-pointer"
                        onClick={() => handleFolderSelect(folder)}
                      >
                        <FolderOpen className="w-6 h-6 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                              {folder.name}
                            </p>
                            {/* Access type indicators */}
                            {folder.accessType === 'shared' && (
                              <div className="flex items-center text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded" title="Shared with you">
                                <Users className="w-3 h-3 mr-0.5" />
                                Shared
                              </div>
                            )}
                            {folder.accessType === 'team-drive' && (
                              <div className="flex items-center text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded" title="Team/Shared Drive">
                                <Shield className="w-3 h-3 mr-0.5" />
                                Team
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {folder.url}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewInDrive(folder);
                        }}
                        className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Open in Google Drive"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {/* Load More Button */}
                  {showLoadMore && (
                    <div className="text-center pt-4">
                      <button
                        onClick={handleLoadMore}
                        className="px-4 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors text-sm"
                      >
                        Load More Folders
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Only folders you have access to will appear in search results
            </p>
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
